/**
 * NOTIFICATION BROADCAST — compose a push, see exactly what it will look like, choose precisely who
 * gets it, and read back what actually happened.
 *
 * What this replaces: a four-field modal (audience / channel / title / body) whose audience was a
 * three-item dropdown — all retailers, all farmers, or "all sales". There was no way to send to one
 * person, no way to address a Warehouse Manager or the Accounts team at all, no picture, no link,
 * no preview, and the only feedback was "Queued 412" whether or not a single message went out.
 *
 * Three things this page refuses to guess at:
 *  • The audience count comes from the server resolving the audience with the SAME code the send
 *    uses, and it reports `reachable` separately — people with a registered device — because that
 *    is the number that will actually receive a push.
 *  • The preview renders the real payload: the image as the handset will crop it, and the deep link
 *    it will open.
 *  • The result is the provider's own counts (sent / failed / skipped), never "queued".
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import {
  Bell, ImagePlus, Link2, Loader2, Search, Send, Trash2, Save, Users, X, ChevronRight,
  AlertTriangle, Check, MapPin, Smartphone, Maximize2, Paperclip, FileText,
} from 'lucide-react';
import { api, request, fileUrl } from '../../api/client';
import { AttachmentViewer } from '../../components/AttachmentPreview';
import { fitImageForUpload, formatBytes } from '../../lib/fitImage';
import { RoleBadge, roleTone } from '../../components/RoleBadge';
import { TAG_TONE_HUE, filterTint } from '../../lib/palette';
import {
  Button, Card, Input, Textarea, Field, Modal, Tag, Select, Segmented, Alert,
  PageHeader, DataTable, DataTableColumn, StatusBadge, EmptyState, EntityAvatar, toast, cn,
} from '../../ui';
import { formatIst } from '../../lib/time';

// ── Types mirroring the API ─────────────────────────────────────────────────────────────────────

type Audience = 'ALL' | 'BY_ROLE' | 'BY_TERRITORY' | 'CUSTOM';

interface RoleRow { code: string; name: string; users: number; reachable: number }
interface LinkTarget {
  code: string; label: string; group: string;
  webPath: string | null; androidRoute: string | null;
  needsId?: true; idLabel?: string; roles?: string[];
}
interface RecipientRow {
  id: string; fullName: string; mobile: string; email: string;
  roleCode: string; territoryName?: string; profilePhotoUrl?: string | null; reachable: boolean;
}
/** A territory as the composer addresses it: its place in the tree, and who it actually reaches. */
interface TerritoryRow {
  id: string; name: string; level: string; parentId: string | null;
  path: string; people: number; reachable: number;
}
interface AudiencePreview {
  total: number; reachable: number;
  sample: { id: string; fullName: string; roleCode: string }[];
}
/** What this deployment can actually deliver — see the backend's broadcast/capabilities. */
interface Capabilities { pushConfigured: boolean; pictureInPush: boolean; publicBaseUrl: string | null }

interface Campaign {
  id: string; title: string; body: string;
  imageUrl: string | null; documentUrl: string | null; documentName: string | null;
  deeplink: string | null;
  channel: string; audience: Audience;
  roleCodes: string[] | null; territoryId: string | null; userIds: string[] | null;
  status: string;
  /** Up to three resolved names for a CUSTOM audience; `userIds.length` says how many there are. */
  audienceNames?: string[];
  territoryName?: string | null;
  recipientCount: number; sentCount: number; failedCount: number; skippedCount: number;
  error: string | null; sentAt: string | null; createdAt: string;
}

const AUDIENCE_TABS: { value: Audience; label: string }[] = [
  { value: 'ALL', label: 'Everyone' },
  { value: 'BY_ROLE', label: 'By role' },
  { value: 'BY_TERRITORY', label: 'By territory' },
  { value: 'CUSTOM', label: 'Specific people' },
];

const EMPTY_DRAFT = {
  id: undefined as string | undefined,
  title: '',
  body: '',
  imageUrl: '',
  documentUrl: '',
  documentName: '',
  deeplink: '',
  linkId: '',
  audience: 'BY_ROLE' as Audience,
  roleCodes: [] as string[],
  territoryId: '',
  userIds: [] as string[],
};
type Draft = typeof EMPTY_DRAFT;

/**
 * One hue per link group, so the picker's sections are told apart by colour before they are read —
 * and the same hue marks the option rows inside a group. Taken from the shared tag palette rather
 * than invented here, so "Billing" is the blue billing wears elsewhere. An unknown group (the list
 * is the server's and may grow) falls back to a stable pick rather than to nothing.
 */
const LINK_GROUP_TONE: Record<string, keyof typeof TAG_TONE_HUE> = {
  General: 'neutral',
  Orders: 'green',
  Billing: 'blue',
  Inventory: 'cyan',
  'Field force': 'gold',
  Complaints: 'red',
  Events: 'purple',
  Catalogue: 'gold',
  People: 'purple',
  Reports: 'blue',
};
const TONE_CYCLE: Array<keyof typeof TAG_TONE_HUE> = ['green', 'blue', 'gold', 'purple', 'cyan', 'red'];
function groupHue(group: string): string {
  const tone = LINK_GROUP_TONE[group]
    ?? TONE_CYCLE[[...group].reduce((a, c) => a + c.charCodeAt(0), 0) % TONE_CYCLE.length];
  return TAG_TONE_HUE[tone];
}

/**
 * THE ATTACHED PICTURE, WHOLE — and openable.
 *
 * It was drawn `object-cover` into a fixed 208px band, so a portrait photo lost its top and bottom
 * and a wide banner lost its ends: the operator could not see what they were about to send, which is
 * the one thing a preview is for. It is `object-contain` on a neutral bed instead — the whole frame,
 * whatever its shape — and a click opens the house full-screen viewer (AttachmentViewer, which also
 * handles Escape correctly when it is opened from inside a dialog).
 *
 * The caption stays honest about the difference between this and the handset: Android's
 * BigPictureStyle crops a tall picture to roughly 2:1, so "shown whole here" is a statement about
 * THIS screen, not a promise about the tray.
 */
function PictureFrame({
  url, thumb, className,
}: { url: string; thumb?: boolean; className?: string }) {
  const [open, setOpen] = useState(false);
  const [broken, setBroken] = useState(false);
  if (!url || broken) return null;
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        title="Open the picture full screen"
        aria-label="Open the attached picture full screen"
        className={cn(
          'group relative cursor-zoom-in overflow-hidden bg-[color-mix(in_srgb,var(--gb-content)_7%,var(--gb-surface))]',
          // A thumbnail is a FIXED BOX the picture fits inside; the wide frame is the other way
          // round — it takes the width it is given and keeps its own height. Sizing the box without
          // also constraining the <img> (as this first did) leaves the picture at its natural height
          // inside a 48px frame, i.e. a clipped band that shows no more than object-cover did.
          thumb ? 'size-12 shrink-0 rounded-control border border-line' : 'block w-full',
          className,
        )}
      >
        <img
          src={url}
          alt="The picture attached to this notification"
          className={thumb ? 'size-full object-contain' : 'mx-auto max-h-72 w-auto max-w-full object-contain'}
          onError={() => setBroken(true)}
        />
        {thumb ? (
          <span className="pointer-events-none absolute inset-0 grid place-items-center bg-black/35 text-white opacity-0 transition-opacity group-hover:opacity-100">
            <Maximize2 size={14} />
          </span>
        ) : (
          <span className="pointer-events-none absolute right-2 top-2 flex items-center gap-1 rounded-pill bg-black/55 px-2 py-0.5 type-caption text-white opacity-0 transition-opacity group-hover:opacity-100">
            <Maximize2 size={11} /> Full screen
          </span>
        )}
      </button>
      {open && (
        <AttachmentViewer
          files={[url]}
          index={0}
          label="Notification picture"
          onIndex={() => undefined}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

/** The stored deeplink: a link template with `{id}` filled in, or the bare code. */
function resolveLink(code: string, id: string): string {
  if (!code) return '';
  return code.includes('{id}') ? (id ? code.replace('{id}', id.trim()) : '') : code;
}

// ── Live preview ────────────────────────────────────────────────────────────────────────────────

/**
 * How the push will look on a handset — the collapsed tray row, and the expanded card with the
 * picture. Deliberately shows BOTH: an image only appears once the notification is expanded, so a
 * preview that showed only the big version would misrepresent what most recipients see first.
 */
function NotificationPreview({ draft, link }: { draft: Draft; link: string }) {
  const doc = draft.documentName || (draft.documentUrl ? 'Attached document' : '');
  const title = draft.title.trim() || 'Notification title';
  const body = draft.body.trim() || 'Your message will appear here…';
  const img = draft.imageUrl ? fileUrl(draft.imageUrl) : '';
  return (
    <div className="space-y-4">
      <div>
        <p className="mb-2 type-label-strong text-content">In the notification tray</p>
        <div className="rounded-card border border-line bg-surface-2 p-3">
          <div className="flex items-start gap-2.5">
            <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md bg-brand-600 text-white">
              <Bell size={15} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="type-caption text-muted">Green Bay Bio · now</p>
              <p className="truncate type-label-strong text-content">{title}</p>
              <p className="line-clamp-1 type-body text-content-2">{body}</p>
            </div>
            {img && <img src={img} alt="" className="size-10 shrink-0 rounded-md object-cover" />}
          </div>
        </div>
      </div>

      <div>
        <p className="mb-2 type-label-strong text-content">Expanded</p>
        <div className="overflow-hidden rounded-card border border-line bg-surface-2">
          <div className="p-3">
            <div className="flex items-start gap-2.5">
              <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md bg-brand-600 text-white">
                <Bell size={15} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="type-caption text-muted">Green Bay Bio · now</p>
                <p className="type-label-strong text-content">{title}</p>
                <p className="whitespace-pre-wrap type-body text-content-2">{body}</p>
              </div>
            </div>
          </div>
          {img && (
            <>
              <PictureFrame url={img} className="border-t border-line" />
              {/* Shown whole above; the handset is the one that crops. Saying which is which keeps
                  the preview from over-promising. */}
              <p className="px-3 pt-1.5 type-caption text-muted">
                Shown whole here — a handset crops it to about 2:1 in the tray.
              </p>
            </>
          )}
          {/* A document is not drawn in the tray by anything — it is named, and the tap opens it.
              Showing it as a row rather than as a picture keeps the preview truthful. */}
          {doc && (
            <div className="flex items-center gap-1.5 border-t border-line px-3 py-2 type-caption text-content-2">
              <FileText size={13} className="shrink-0 text-brand-600" />
              <span className="truncate">{doc}</span>
              <Tag tone="blue">opens on tap</Tag>
            </div>
          )}
          {link !== '' && (
            <div className="flex items-center gap-1.5 border-t border-line px-3 py-2 type-caption text-muted">
              <Link2 size={12} />
              <span className="truncate">Tapping opens <span className="text-content-2">{link || 'app home'}</span></span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Territory picker ────────────────────────────────────────────────────────────────────────────

/**
 * STATE ▸ DISTRICT ▸ BEAT, never one flat list.
 *
 * The composer offered all 74 territories in a single dropdown: four "Junnar"-shaped beats sitting
 * between sixty districts and nine states, with no way to say "Maharashtra" without first knowing
 * that Maharashtra is a state and Junnar is not. That is the same flat-beat-list problem every other
 * geography filter in this app already solved — territory pickers here are a cascade
 * (see lib/useStaffTerritories): the state is picked first, the district appears only once it is,
 * and the beat only once the district is.
 *
 * WHAT IS ADDRESSED IS THE DEEPEST NODE PICKED, which is why "All districts in Maharashtra" is a
 * real option carrying the STATE's own id rather than a clear-✕: choosing it addresses Maharashtra,
 * and the resolver already includes everything beneath it. Narrowing then widening can therefore
 * never leave a stale district quietly filtering underneath a different state — there is only ever
 * one stored value, and the three controls are derived from it.
 */
function TerritoryCascade({
  territories, value, onChange,
}: { territories: TerritoryRow[]; value: string; onChange: (id: string) => void }) {
  const byId = useMemo(() => new Map(territories.map((t) => [t.id, t])), [territories]);
  const childrenOf = useMemo(() => {
    const m = new Map<string, TerritoryRow[]>();
    for (const t of territories) {
      const k = t.parentId ?? '';
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(t);
    }
    for (const list of m.values()) list.sort((a, b) => a.name.localeCompare(b.name));
    return m;
  }, [territories]);

  /** The three levels, read back OFF the stored id by walking up the tree — no second source of truth. */
  const pick = useMemo(() => {
    const out: { state: string | null; district: string | null; beat: string | null } = {
      state: null, district: null, beat: null,
    };
    const seen = new Set<string>();
    let cur = value ? byId.get(value) : undefined;
    while (cur && !seen.has(cur.id)) {
      seen.add(cur.id);
      if (cur.level === 'beat') out.beat = cur.id;
      else if (cur.level === 'district') out.district = cur.id;
      else if (cur.level === 'state') out.state = cur.id;
      cur = cur.parentId ? byId.get(cur.parentId) : undefined;
    }
    return out;
  }, [value, byId]);

  const states = useMemo(
    () => territories.filter((t) => t.level === 'state').sort((a, b) => a.name.localeCompare(b.name)),
    [territories],
  );
  const districts = pick.state ? (childrenOf.get(pick.state) ?? []) : [];
  const beats = pick.district ? (childrenOf.get(pick.district) ?? []) : [];

  /** Every option says who it reaches, so an empty branch is visible before it is chosen. */
  const reach = (t: TerritoryRow) => (
    <span className="flex w-full items-center gap-2">
      <MapPin size={13} className="shrink-0 text-brand-600" />
      <span className="min-w-0 flex-1 truncate type-body text-content">{t.name}</span>
      <Tag tone={t.people > 0 ? 'green' : 'neutral'}>
        {t.people} {t.people === 1 ? 'person' : 'people'}
      </Tag>
      {t.people > 0 && <Tag tone={t.reachable > 0 ? 'blue' : 'warning'}>{t.reachable} with a device</Tag>}
    </span>
  );
  const opt = (t: TerritoryRow) => ({
    value: t.id, searchLabel: `${t.name} ${t.path}`, selectedText: t.name, label: reach(t),
  });
  /** "All districts in Maharashtra" — carries the PARENT's id, because that is what gets addressed. */
  const allOpt = (parent: TerritoryRow, what: string) => ({
    value: parent.id,
    searchLabel: `all ${what} ${parent.name}`,
    selectedText: `All ${what} in ${parent.name}`,
    label: (
      <span className="flex w-full items-center gap-2">
        <Users size={13} className="shrink-0 text-brand-600" />
        <span className="min-w-0 flex-1 truncate type-body text-content">All {what} in {parent.name}</span>
        <Tag tone={parent.people > 0 ? 'green' : 'neutral'}>{parent.people} people</Tag>
      </span>
    ),
  });

  const stateNode = pick.state ? byId.get(pick.state) : undefined;
  const districtNode = pick.district ? byId.get(pick.district) : undefined;
  const chosen = value ? byId.get(value) : undefined;

  return (
    <div className="space-y-2.5">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 2xl:grid-cols-3">
        <Field label="State" className="mb-0">
          <Select
            value={pick.state ?? undefined}
            onChange={(v) => onChange((v as string) ?? '')}
            showSearch
            allowClear
            placeholder="Pick a state"
            optionFilterProp="searchLabel"
            optionLabelProp="selectedText"
            options={states.map(opt)}
          />
        </Field>

        {/* The district appears only once a state is chosen, and the beat only once a district is —
            a level that is not on screen must never be narrowing the audience. */}
        {stateNode && districts.length > 0 && (
          <Field label="District" className="mb-0">
            <Select
              value={pick.district ?? pick.state ?? undefined}
              onChange={(v) => onChange((v as string) ?? pick.state ?? '')}
              showSearch
              placeholder="All districts"
              optionFilterProp="searchLabel"
              optionLabelProp="selectedText"
              options={[allOpt(stateNode, 'districts'), ...districts.map(opt)]}
            />
          </Field>
        )}

        {districtNode && beats.length > 0 && (
          <Field label="Beat" className="mb-0">
            <Select
              value={pick.beat ?? pick.district ?? undefined}
              onChange={(v) => onChange((v as string) ?? pick.district ?? '')}
              showSearch
              placeholder="All beats"
              optionFilterProp="searchLabel"
              optionLabelProp="selectedText"
              options={[allOpt(districtNode, 'beats'), ...beats.map(opt)]}
            />
          </Field>
        )}
      </div>

      {/* What is actually addressed, spelt out — the three controls above show three names, and which
          of them the message is going to is the one fact none of them states on its own. */}
      {chosen ? (
        <p className="type-caption text-muted">
          Going to <span className="text-content-2">{chosen.path}</span> — its staff, retailers and
          farmers, and everything beneath it.
        </p>
      ) : (
        <p className="type-caption text-muted">
          Pick a state to start. A district and a beat appear as you narrow; stopping at any level
          addresses everything under it.
        </p>
      )}
    </div>
  );
}

// ── Recipient picker ────────────────────────────────────────────────────────────────────────────

function RecipientPicker({
  selected, onChange,
}: { selected: RecipientRow[]; onChange: (v: RecipientRow[]) => void }) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 300);
    return () => clearTimeout(t);
  }, [term]);

  const { data: results = [], isFetching, isError, error } = useQuery({
    queryKey: ['notif-recipients', debounced],
    queryFn: () => request<RecipientRow[]>({ url: '/notifications/recipients/search', params: { q: debounced } }),
    enabled: debounced.length >= 2,
  });

  const add = (u: RecipientRow) => {
    if (!selected.some((s) => s.id === u.id)) onChange([...selected, u]);
    setTerm('');
  };

  return (
    <div className="space-y-2.5">
      <div className="relative">
        <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
        <Input
          value={term}
          onChange={(e) => setTerm(e.currentTarget.value)}
          placeholder="Name, mobile or email — spaces and +91 are fine" aria-label="Search by name, mobile or email"
          className="pl-9"
        />
        {/* One shadow on the panel below: cn() carries no tailwind-merge, so `shadow-e1 shadow-lg`
            left two competing rules to stylesheet order. shadow-e1 is the house elevation token. */}
        {debounced.length >= 2 && (
          <div className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-card border border-line bg-surface shadow-e1">
            {isFetching && <div className="px-3 py-2 type-caption text-muted">Searching…</div>}
            {/*
              A FAILED SEARCH AND AN EMPTY ONE ARE DIFFERENT ANSWERS.
              React Query hands back `undefined` on an error, which this list used to render as
              "Nobody matches …" — so an API that was down, or a session that had expired, was
              reported as "that person is not on the platform". The operator then hunts for the
              person instead of for the outage.
            */}
            {!isFetching && isError && (
              <div className="flex items-start gap-2 px-3 py-2 type-caption text-danger-600">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>
                  The search could not run — {(error as any)?.response?.data?.error?.message ?? (error as any)?.message ?? 'the server did not answer'}.
                  This is not an answer about {debounced}; try again.
                </span>
              </div>
            )}
            {!isFetching && !isError && results.length === 0 && (
              <div className="px-3 py-2 type-caption text-muted">
                Nobody active matches “{debounced}”.
              </div>
            )}
            {!isError && results.map((u) => (
              <button
                key={u.id}
                type="button"
                onClick={() => add(u)}
                className="flex w-full items-center gap-2 border-b border-line/60 px-3 py-2 text-left last:border-0 hover:bg-surface-2"
              >
                {/* The role's own hue on the left edge: a mixed list of reps, dealers and farmers is
                    read by colour before it is read by word. */}
                <span
                  className="h-9 w-1 shrink-0 rounded-full"
                  style={{ backgroundColor: TAG_TONE_HUE[roleTone(u.roleCode)] ?? undefined }}
                />
                {/* Their own photograph — four people called "Super Admin" are told apart by face
                    long before anyone reads the four mobile numbers underneath. Falls back to the
                    initials mark used everywhere else when there is no photo, or it 404s. */}
                <EntityAvatar name={u.fullName || u.mobile} src={u.profilePhotoUrl ?? undefined} size={32} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate type-body text-content">{u.fullName || '(no name)'}</span>
                  <span className="block truncate type-caption text-muted">
                    {u.mobile}
                    {u.territoryName ? ` · ${u.territoryName}` : ''}
                  </span>
                </span>
                <RoleBadge role={u.roleCode} />
                {/* Says up front who cannot be pushed to, rather than letting them land in the skipped count. */}
                {u.reachable
                  ? <Tag tone="green"><Smartphone size={11} /> device</Tag>
                  : <Tag tone="warning">no device</Tag>}
              </button>
            ))}
          </div>
        )}
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((u) => (
            <span
              key={u.id}
              className="inline-flex items-center gap-1.5 rounded-full border py-1 pl-1 pr-2.5 type-caption text-content"
              style={filterTint(TAG_TONE_HUE[roleTone(u.roleCode)])}
            >
              <EntityAvatar name={u.fullName || u.mobile} src={u.profilePhotoUrl ?? undefined} size={20} />
              {u.fullName || u.mobile}
              <RoleBadge role={u.roleCode} />
              {!u.reachable && <span className="text-warning-600">· no device</span>}
              <button
                type="button"
                onClick={() => onChange(selected.filter((s) => s.id !== u.id))}
                className="text-muted hover:text-danger-600"
                aria-label={`Remove ${u.fullName}`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────────────────────────

export function BroadcastComposerPage() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [picked, setPicked] = useState<RecipientRow[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [openCampaign, setOpenCampaign] = useState<Campaign | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const docRef = useRef<HTMLInputElement>(null);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const { data: roles = [] } = useQuery({
    queryKey: ['notif-audience-roles'],
    queryFn: () => request<RoleRow[]>({ url: '/notifications/audience/roles' }),
  });
  const { data: caps } = useQuery<Capabilities>({
    queryKey: ['notif-broadcast-caps'],
    queryFn: () => request<Capabilities>({ url: '/notifications/broadcast/capabilities' }),
  });
  const { data: links = [] } = useQuery({
    queryKey: ['notif-links'],
    queryFn: () => request<LinkTarget[]>({ url: '/notifications/links' }),
  });
  // Served by the broadcast controller, not /territories: that one needs TERRITORY.VIEW, which an
  // operator holding only NOTIF.BROADCAST does not have — they saw an empty picker. It also carries
  // the reach of each territory, resolved by the same code the send runs.
  const { data: territories = [] } = useQuery({
    queryKey: ['notif-audience-territories'],
    queryFn: () => request<TerritoryRow[]>({ url: '/notifications/audience/territories' }),
  });
  const { data: campaigns, isLoading: loadingCampaigns } = useQuery({
    queryKey: ['notif-campaigns'],
    queryFn: () => request<{ items: Campaign[] }>({ url: '/notifications/campaigns' }),
  });

  /**
   * The roles the message is actually going to, where that is knowable — used to flag a deep link
   * the recipients have no screen for. "Everyone" and a whole territory are mixed populations, so
   * they narrow nothing and no link is flagged.
   */
  const audienceRoles = useMemo(() => (
    draft.audience === 'BY_ROLE' ? draft.roleCodes
      : draft.audience === 'CUSTOM' ? [...new Set(picked.map((p) => p.roleCode).filter(Boolean))]
        : []
  ), [draft.audience, draft.roleCodes, picked]);

  const selectedLink = links.find((l) => l.code === draft.deeplink);
  /** The chosen link exists for none of the roles being addressed — it would open nothing for them. */
  const linkUnreachable = !!selectedLink?.roles && audienceRoles.length > 0
    && !audienceRoles.some((rc) => selectedLink.roles!.includes(rc));
  const resolvedLink = resolveLink(draft.deeplink, draft.linkId);
  // A link template whose id is still missing would deep-link every recipient to a screen that
  // cannot load, so it blocks the send rather than going out broken.
  const linkIncomplete = !!selectedLink?.needsId && !draft.linkId.trim();

  // ── Audience preview: resolved server-side, by the same code the send will run ────────────────
  const audienceBody = useMemo(() => ({
    audience: draft.audience,
    roleCodes: draft.audience === 'BY_ROLE' ? draft.roleCodes : undefined,
    territoryId: draft.audience === 'BY_TERRITORY' ? (draft.territoryId || undefined) : undefined,
    userIds: draft.audience === 'CUSTOM' ? picked.map((p) => p.id) : undefined,
  }), [draft.audience, draft.roleCodes, draft.territoryId, picked]);

  const audienceReady =
    draft.audience === 'ALL'
    || (draft.audience === 'BY_ROLE' && draft.roleCodes.length > 0)
    || (draft.audience === 'BY_TERRITORY' && !!draft.territoryId)
    || (draft.audience === 'CUSTOM' && picked.length > 0);

  const { data: audience, isFetching: countingAudience } = useQuery({
    queryKey: ['notif-audience-preview', audienceBody],
    queryFn: () => request<AudiencePreview>({
      url: '/notifications/audience/preview', method: 'POST', data: audienceBody,
    }),
    enabled: audienceReady,
  });

  // ── Image upload ─────────────────────────────────────────────────────────────────────────────
  /**
   * A picture too big for FCM is RESIZED, not refused.
   *
   * This used to compare `file.size` against 2 MB and stop there, which meant every photograph taken
   * on a phone — 4 MB and up, all of them — ended at "Image too large", with no way forward from
   * inside the app. The browser does the resize (see fitImageForUpload) and the operator is told
   * what it did, so the 2 MB ceiling stays honest without being a wall.
   */
  const onPickImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    /*
     * A DOCUMENT IS NOT A PICTURE, AND SAYING SO IS MORE USE THAN FAILING TO DECODE IT.
     *
     * The file dialog filters to images, but "All files" is one click away in it, and a PDF price
     * list is exactly the thing someone reaches for when they want to broadcast one. A push carries
     * a PICTURE — Android's BigPictureStyle and FCM's own renderer decode an image and nothing else,
     * so a PDF attached here would arrive as a notification with no picture and no explanation. The
     * way to send a document is to link to the screen that holds it, which is what "Open when
     * tapped" is for, so that is what the message says.
     */
    if (file.type && !file.type.startsWith('image/')) {
      toast.error(
        `That is a ${file.name.split('.').pop()?.toUpperCase() || 'document'}, and only a picture can be `
        + 'RENDERED in a notification. Attach it under "Document" instead — it travels with the '
        + 'notification and opens when tapped.',
      );
      return;
    }
    setUploading(true);
    try {
      const fitted = await fitImageForUpload(file, { maxBytes: 2 * 1024 * 1024 });
      if (fitted.changed) {
        toast.info(
          `Picture resized to ${fitted.width}×${fitted.height} (${formatBytes(fitted.originalBytes)} → ${formatBytes(fitted.bytes)}) so FCM will deliver it.`,
        );
      }
      const form = new FormData();
      form.append('file', fitted.file);
      const r = await api.post('/uploads/notification-image', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = (r.data?.data ?? r.data)?.url;
      if (!url) throw new Error('The server did not return a URL');
      set('imageUrl', url);
      toast.success('Image uploaded');
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message ?? err.message ?? 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  /**
   * A DOCUMENT ATTACHED TO THE BROADCAST — carried by it, opened on tap.
   *
   * Deliberately a separate control from the picture, because the two are delivered differently and
   * pretending otherwise is what made "can I send a PDF?" unanswerable: a picture is RENDERED inside
   * the notification (and so is bound by FCM's 2 MB and its image-only decoder), while a document is
   * only ever a thing the notification POINTS AT. Keeping them apart lets each say its own truth.
   */
  const onPickDocument = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type.startsWith('image/')) {
      toast.error('That is a picture — attach it under "Picture" so it shows inside the notification.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(`That document is ${formatBytes(file.size)} — the limit is 10 MB, because it is opened over a field phone's data plan.`);
      return;
    }
    setUploadingDoc(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await api.post('/uploads/notification-doc', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = (r.data?.data ?? r.data)?.url;
      if (!url) throw new Error('The server did not return a URL');
      setDraft((d) => ({ ...d, documentUrl: url, documentName: file.name }));
      toast.success(`${file.name} attached — tapping the notification opens it`);
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message ?? err.message ?? 'Could not attach the document');
    } finally {
      setUploadingDoc(false);
    }
  };

  // ── Save / send ──────────────────────────────────────────────────────────────────────────────
  const payload = () => ({
    title: draft.title.trim(),
    body: draft.body.trim(),
    imageUrl: draft.imageUrl || null,
    documentUrl: draft.documentUrl || null,
    documentName: draft.documentName || null,
    deeplink: resolvedLink || null,
    channel: 'FCM',
    audience: draft.audience,
    roleCodes: draft.audience === 'BY_ROLE' ? draft.roleCodes : undefined,
    territoryId: draft.audience === 'BY_TERRITORY' ? draft.territoryId : undefined,
    userIds: draft.audience === 'CUSTOM' ? picked.map((p) => p.id) : undefined,
  });

  const saveDraft = useConfirmedMutation({ title: "Save this broadcast draft?", content: "Nothing is sent — it is saved so you can send it later.", okText: "Yes, save" }, {
    mutationFn: () => draft.id
      ? request<Campaign>({ url: `/notifications/campaigns/${draft.id}`, method: 'PUT', data: payload() })
      : request<Campaign>({ url: '/notifications/campaigns', method: 'POST', data: payload() }),
    onSuccess: (c) => {
      set('id', c.id);
      toast.success('Draft saved — nothing has been sent');
      qc.invalidateQueries({ queryKey: ['notif-campaigns'] });
    },
    onError: (e: any) => toast.error(e.response?.data?.error?.message ?? 'Could not save the draft'),
  });

  const sendNow = useMutation({
    mutationFn: async () => {
      // Always persist first: what went out is then a record, not a request body that vanished.
      const c = draft.id
        ? await request<Campaign>({ url: `/notifications/campaigns/${draft.id}`, method: 'PUT', data: payload() })
        : await request<Campaign>({ url: '/notifications/campaigns', method: 'POST', data: payload() });
      return request<any>({ url: `/notifications/campaigns/${c.id}/send`, method: 'POST' });
    },
    onSuccess: (r) => {
      // The provider's own counts, not "queued".
      const parts = [`${r.sent} delivered to the provider`];
      if (r.failed) parts.push(`${r.failed} failed`);
      if (r.skipped) parts.push(`${r.skipped} skipped`);
      const msg = `${parts.join(', ')} (of ${r.recipients} recipients)`;
      if (r.sent > 0) toast.success(msg);
      else toast.error(`${msg}${r.error ? ` — ${r.error}` : ''}`);
      setDraft(EMPTY_DRAFT);
      setPicked([]);
      qc.invalidateQueries({ queryKey: ['notif-campaigns'] });
    },
    onError: (e: any) => toast.error(e.response?.data?.error?.message ?? 'The broadcast failed'),
  });

  const removeCampaign = useConfirmedMutation({ title: "Delete this draft?", content: "It can’t be recovered.", okText: "Yes, delete", danger: true }, {
    mutationFn: (id: string) => request({ url: `/notifications/campaigns/${id}`, method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Draft deleted');
      qc.invalidateQueries({ queryKey: ['notif-campaigns'] });
    },
    onError: (e: any) => toast.error(e.response?.data?.error?.message ?? 'Could not delete'),
  });

  const composeReady = draft.title.trim() && draft.body.trim() && audienceReady && !linkIncomplete;

  const confirmSend = () => {
    if (!composeReady) return;
    const n = audience?.reachable ?? 0;
    Modal.confirm({
      title: 'Send this notification now?',
      content: (
        <div className="space-y-2">
          <p>
            It goes to <b>{audience?.total ?? 0}</b> {audience?.total === 1 ? 'person' : 'people'}
            {n !== (audience?.total ?? 0) && (
              <> — <b>{n}</b> of whom {n === 1 ? 'has' : 'have'} a registered device and can actually receive it</>
            )}.
          </p>
          <p className="text-muted">A push cannot be recalled once it has left.</p>
        </div>
      ),
      okText: 'Send now',
      onOk: () => sendNow.mutateAsync(),
    });
  };

  const loadDraft = (c: Campaign) => {
    const match = links.find((l) => l.code === c.deeplink)
      ?? links.find((l) => l.needsId && c.deeplink?.startsWith(l.code.slice(0, l.code.indexOf('{'))));
    setDraft({
      id: c.status === 'DRAFT' ? c.id : undefined, // a sent campaign is duplicated, never re-sent
      title: c.title,
      body: c.body,
      imageUrl: c.imageUrl ?? '',
      documentUrl: c.documentUrl ?? '',
      documentName: c.documentName ?? '',
      deeplink: match?.code ?? '',
      linkId: match?.needsId && c.deeplink ? c.deeplink.slice(match.code.indexOf('{')) : '',
      audience: c.audience,
      roleCodes: c.roleCodes ?? [],
      territoryId: c.territoryId ?? '',
      userIds: c.userIds ?? [],
    });
    setPicked([]);
    setOpenCampaign(null);
    if (c.audience === 'CUSTOM') {
      toast.info('Re-pick the recipients — a saved list is not re-expanded into the picker.');
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const columns: DataTableColumn<Campaign>[] = [
    { key: 'title', title: 'Title', dataIndex: 'title', sortable: true, alwaysVisible: true },
    {
      /*
        WHO, BY NAME — a register that said "1 specific person" and "One territory" withheld the one
        fact the reader opened it for. Roles wear their own badges here (the same ones the composer
        and the people list use), a named audience prints its names, and a territory prints itself.
      */
      key: 'audience', title: 'Audience', dataIndex: 'audience',
      render: (v: Audience, r) => {
        if (v === 'BY_ROLE') {
          const codes = r.roleCodes ?? [];
          return codes.length
            ? <span className="flex flex-wrap items-center gap-1">{codes.map((c) => <RoleBadge key={c} role={c} />)}</span>
            : <span className="type-caption text-muted">By role</span>;
        }
        if (v === 'CUSTOM') {
          const names = r.audienceNames ?? [];
          const total = (r.userIds ?? []).length;
          if (!names.length) return <span className="type-caption text-content-2">{total} named {total === 1 ? 'person' : 'people'}</span>;
          const more = total - names.length;
          return (
            <span className="type-caption text-content-2">
              {names.join(', ')}{more > 0 && <span className="text-muted"> +{more} more</span>}
            </span>
          );
        }
        if (v === 'BY_TERRITORY') {
          return (
            <span className="flex items-center gap-1.5 type-caption text-content-2">
              <MapPin size={12} className="shrink-0 text-brand-600" />
              {r.territoryName || 'One territory'}
            </span>
          );
        }
        return <span className="type-caption text-content-2">Everyone</span>;
      },
    },
    {
      key: 'status', title: 'Status', dataIndex: 'status', sortable: true,
      render: (v: string) => <StatusBadge status={v} />,
    },
    {
      key: 'delivery', title: 'Delivery',
      render: (_: unknown, r) => r.status === 'DRAFT'
        ? <span className="type-caption text-muted">not sent</span>
        : (
          <span className="type-caption text-content-2">
            {r.sentCount} sent
            {r.failedCount > 0 && <span className="text-danger-600"> · {r.failedCount} failed</span>}
            {r.skippedCount > 0 && <span className="text-warning-600"> · {r.skippedCount} skipped</span>}
            <span className="text-muted"> of {r.recipientCount}</span>
          </span>
        ),
    },
    {
      key: 'sentAt', title: 'Sent', dataIndex: 'sentAt', sortable: true,
      render: (v: string | null) => v ? formatIst(v) : '—',
    },
    {
      // The ROW opens the record (see `onRow`), so there is no "open" button here — only the one
      // action the row click must not trigger. Deleting stops the event, or clicking the bin would
      // also open the thing it is deleting.
      key: 'actions', title: '', width: 70,
      render: (_: unknown, r) => (
        <div className="flex items-center justify-end gap-1">
          {r.status === 'DRAFT' && (
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => { e.stopPropagation(); removeCampaign.mutate(r.id); }}
              aria-label={`Delete the draft “${r.title}”`}
            >
              <Trash2 size={15} className="text-danger-600" />
            </Button>
          )}
          <ChevronRight size={15} className="text-muted" aria-hidden />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Notification broadcast"
        subtitle="Compose a push, choose exactly who receives it, and see it before it goes."
        icon={<Bell size={18} />}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="default"
              size="sm"
              disabled={!composeReady || saveDraft.isPending}
              loading={saveDraft.isPending}
              onClick={() => saveDraft.mutate()}
            >
              <Save size={14} /> Save draft
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={!composeReady || sendNow.isPending}
              loading={sendNow.isPending}
              onClick={confirmSend}
            >
              <Send size={14} /> Send now
            </Button>
          </div>
        }
      />

      {/*
        THREE COLUMNS, NOT TWO STACKED ONES.
        The compose card and the audience card used to share a 2/3-wide column, one under the other:
        the message ran the full width of a 1900px screen for a 200-character title, the nine role
        tiles sat below the fold, and the whole right-hand side under the preview was empty. Writing
        the message, choosing who gets it and seeing the result are three parts of one decision, so
        they sit side by side and the page stops being a scroll. Below `xl` they fall back to two
        columns and then to a single stack, which is what a laptop and a phone can actually hold.
      */}
      <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-2 xl:grid-cols-3">
        {/* ── Compose ────────────────────────────────────────────────────────────────────── */}
        <div className="space-y-5">
          <Card>
            <p className="mb-3 type-label-strong text-content">Message</p>
            <div className="space-y-3.5">
              <Field label="Title" required>
                <Input
                  value={draft.title}
                  maxLength={200}
                  onChange={(e) => set('title', e.currentTarget.value)}
                  placeholder="e.g. Diwali scheme live from today"
                />
              </Field>
              <Field label="Message" required>
                <Textarea
                  rows={4}
                  value={draft.body}
                  maxLength={1000}
                  onChange={(e) => set('body', e.currentTarget.value)}
                  placeholder="Keep it to a couple of lines — the tray truncates the rest."
                />
              </Field>

              <Field
                label="Picture"
                hint="PNG, JPG or WebP — pick any size, a big photo is resized here to the 2 MB FCM will deliver. Uploaded to this server and attached as a public link, because FCM fetches the image itself and cannot log in."
              >
                <div className="flex flex-wrap items-center gap-2">
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={onPickImage} />
                  <Button size="sm" variant="default" disabled={uploading} onClick={() => fileRef.current?.click()}>
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
                    {draft.imageUrl ? 'Replace picture' : 'Upload picture'}
                  </Button>
                  {draft.imageUrl && (
                    <>
                      {/* The picture itself, not just its path — the stored name is machine-made and
                          says nothing about which of three banners is attached. */}
                      <PictureFrame url={fileUrl(draft.imageUrl)} thumb />
                      {/*
                        A PICTURE THAT WILL NOT ARRIVE SAYS SO BEFORE THE SEND.
                        FCM fetches the image from its own servers, so it needs an ABSOLUTE, publicly
                        reachable URL — `PUBLIC_API_BASE_URL`. Unset, the server drops the picture
                        from the push and logs it where nobody is looking: the operator attaches a
                        banner, sends, and is told nothing while every handset shows a plain
                        notification. The one place this is worth knowing is here.
                      */}
                      {caps && !caps.pictureInPush && (
                        <span className="flex items-center gap-1.5 type-caption text-warning-600">
                          <AlertTriangle size={13} className="shrink-0" />
                          This server has no public address set (PUBLIC_API_BASE_URL), so the picture is
                          dropped from the push — only the in-app inbox will show it.
                        </span>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => set('imageUrl', '')}>
                        <X size={14} /> Remove
                      </Button>
                    </>
                  )}
                </div>
              </Field>

              {/*
                THE DOCUMENT — its own control, because it is delivered differently to the picture.
                Nothing renders a PDF inside a notification, on any push transport; what a document
                can do is travel with the message and open when the person taps it. Saying that here,
                beside the control, is the answer to "can we send the price list?" — which the old
                picture-only field could only answer by refusing the file.
              */}
              <Field
                label="Document"
                hint="PDF, Word, Excel or PowerPoint up to 10 MB. It is not shown inside the notification — no handset can render one — it opens when the notification is tapped."
              >
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    ref={docRef}
                    type="file"
                    accept="application/pdf,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                    hidden
                    onChange={onPickDocument}
                  />
                  <Button size="sm" variant="default" disabled={uploadingDoc} onClick={() => docRef.current?.click()}>
                    {uploadingDoc ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />}
                    {draft.documentUrl ? 'Replace document' : 'Attach a document'}
                  </Button>
                  {draft.documentUrl && (
                    <>
                      <a
                        href={fileUrl(draft.documentUrl)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-pill border border-line bg-surface-2 px-2.5 py-1 type-caption text-content hover:border-brand-400"
                      >
                        <FileText size={13} className="text-brand-600" />
                        <span className="max-w-56 truncate">{draft.documentName || 'Document'}</span>
                      </a>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDraft((d) => ({ ...d, documentUrl: '', documentName: '' }))}
                      >
                        <X size={14} /> Remove
                      </Button>
                    </>
                  )}
                </div>
              </Field>

              <Field
                label="Open when tapped"
                hint="Where the app goes when someone taps the notification. The list is the server's — every target here is a screen the apps actually have."
              >
                <div className="flex flex-wrap gap-2">
                  {/*
                    THE LINK LIST SAYS WHAT EACH TARGET IS, NOT JUST ITS NAME.
                    26 one-word rows under grey headings ("Invoices", "Payments", "A specific
                    invoice") read alike and gave no way to tell a screen the audience can open from
                    one it cannot, or a web-only screen from one the app has. Each option now carries
                    its own logical path, its group colour, and a warning when the people being
                    addressed have no such screen — so the wrong link is visible before it is sent,
                    not after it lands nowhere.
                  */}
                  <Select
                    value={draft.deeplink || undefined}
                    onChange={(v) => setDraft((d) => ({ ...d, deeplink: v ?? '', linkId: '' }))}
                    allowClear
                    showSearch
                    placeholder="Nothing — just opens the app"
                    className="min-w-[18rem] flex-1"
                    optionFilterProp="searchLabel"
                    optionLabelProp="selectedText"
                    options={Object.entries(
                      links.reduce<Record<string, LinkTarget[]>>((acc, l) => {
                        (acc[l.group] ??= []).push(l);
                        return acc;
                      }, {}),
                    ).map(([group, items]) => ({
                      label: (
                        <span className="flex items-center gap-1.5">
                          <span className="size-2 rounded-full" style={{ backgroundColor: groupHue(group) }} />
                          {group}
                        </span>
                      ),
                      options: items.map((l) => {
                        const blind = audienceRoles.length > 0 && !!l.roles
                          && !audienceRoles.some((rc) => l.roles!.includes(rc));
                        return {
                          value: l.code,
                          searchLabel: `${group} ${l.label} ${l.code} ${l.webPath ?? ''} ${l.androidRoute ?? ''}`,
                          selectedText: l.label,
                          label: (
                            <span className="flex w-full items-center gap-2">
                              <span className="w-1 self-stretch rounded-full" style={{ backgroundColor: groupHue(group) }} />
                              <span className="min-w-0 flex-1">
                                <span className="block truncate type-body text-content">{l.label}</span>
                                <span className="block truncate type-caption text-muted">
                                  {l.code ? `/${l.code}` : 'the app home screen'}
                                </span>
                              </span>
                              {l.needsId && <Tag tone="gold">needs an id</Tag>}
                              {!l.androidRoute && <Tag tone="neutral">web only</Tag>}
                              {!l.webPath && <Tag tone="neutral">app only</Tag>}
                              {blind && <Tag tone="warning">not for this audience</Tag>}
                            </span>
                          ),
                        };
                      }),
                    }))}
                  />
                  {selectedLink?.needsId && (
                    <Input
                      value={draft.linkId}
                      onChange={(e) => set('linkId', e.currentTarget.value)}
                      placeholder={selectedLink.idLabel ?? 'Id'}
                      className="w-40"
                      invalid={linkIncomplete}
                    />
                  )}
                </div>
                {linkIncomplete && (
                  <p className="mt-1.5 type-caption text-danger-600">
                    {selectedLink?.idLabel ?? 'An id'} is required — without it the link would open a screen that cannot load.
                  </p>
                )}
                {selectedLink && !selectedLink.androidRoute && (
                  <p className="mt-1.5 type-caption text-warning-600">
                    This screen only exists on the web. Recipients on the mobile app will land on the app home instead.
                  </p>
                )}
                {linkUnreachable && (
                  <p className="mt-1.5 flex items-start gap-1.5 type-caption text-warning-600">
                    <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                    <span>
                      “{selectedLink?.label}” is not a screen {audienceRoles.join(', ')} can open — tapping the
                      notification would leave them on the app home. Pick a target they have, or send it without a link.
                    </span>
                  </p>
                )}
              </Field>
            </div>
          </Card>
        </div>

        {/* ── Audience — its own column, so the role grid is not pushed below the fold ────── */}
        <div className="space-y-5">
          <Card>
            <p className="mb-3 type-label-strong text-content">Who receives it</p>
            <Segmented
              value={draft.audience}
              onChange={(v) => setDraft((d) => ({ ...d, audience: v as Audience }))}
              options={AUDIENCE_TABS}
            />

            <div className="mt-4">
              {draft.audience === 'ALL' && (
                <p className="type-body text-content-2">
                  Every active user on the platform, in every role.
                </p>
              )}

              {/*
                EACH ROLE WEARS ITS OWN COLOUR — the same hue it wears as a badge everywhere else
                (see RoleBadge), so the tile you are picking and the pill beside a person's name in
                the list below agree. Nine identical grey cards had to be read word by word; a
                coloured strip and the role's own pill make the grid scannable, and the selected
                tiles are visible at a glance from across the page. A role with nobody in it is
                dimmed rather than hidden — "0 active" is a fact worth seeing before you wonder why
                the audience came to nothing.
              */}
              {draft.audience === 'BY_ROLE' && (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
                  {roles.map((r) => {
                    const on = draft.roleCodes.includes(r.code);
                    const hue = TAG_TONE_HUE[roleTone(r.code)];
                    const empty = r.users === 0;
                    return (
                      <button
                        key={r.code}
                        type="button"
                        onClick={() => set(
                          'roleCodes',
                          on ? draft.roleCodes.filter((c) => c !== r.code) : [...draft.roleCodes, r.code],
                        )}
                        className={cn(
                          'group relative flex items-start gap-2.5 overflow-hidden rounded-card border px-3 py-2.5 text-left transition-all',
                          on ? 'shadow-sm ring-1' : 'border-line hover:-translate-y-px hover:shadow-sm',
                          empty && !on && 'opacity-60',
                        )}
                        style={on
                          ? { ...filterTint(hue), boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${hue} 55%, transparent)` }
                          : undefined}
                        aria-pressed={on}
                      >
                        <span className="w-1 self-stretch rounded-full" style={{ backgroundColor: hue }} />
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5">
                            <span className="min-w-0 flex-1 truncate type-label-strong text-content">{r.name}</span>
                            <RoleBadge role={r.code} />
                          </span>
                          {/* Both numbers, because "40 users" and "6 who can be pushed to" are different facts. */}
                          <span className="mt-0.5 flex flex-wrap items-center gap-x-1.5 type-caption text-muted">
                            <span>{r.users} active</span>
                            <span>·</span>
                            <span className={cn(r.reachable > 0 ? 'text-content-2' : 'text-warning-600')}>
                              {r.reachable} with a device
                            </span>
                          </span>
                        </span>
                        {on && (
                          <span
                            className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full text-white"
                            style={{ backgroundColor: hue }}
                          >
                            <Check size={13} strokeWidth={3} />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/*
                A TERRITORY STATES ITS OWN REACH IN THE LIST, and is reached through the house's
                State ▸ District ▸ Beat cascade rather than one flat list of all 74.
              */}
              {draft.audience === 'BY_TERRITORY' && (
                <TerritoryCascade
                  territories={territories}
                  value={draft.territoryId}
                  onChange={(id) => set('territoryId', id)}
                />
              )}

              {draft.audience === 'CUSTOM' && (
                <RecipientPicker selected={picked} onChange={setPicked} />
              )}
            </div>

            {/* Resolved server-side by the same code the send uses. */}
            {audienceReady && (
              <div className="mt-4 rounded-card border border-line bg-surface-2 px-3 py-2.5">
                {countingAudience ? (
                  <span className="inline-flex items-center gap-2 type-body text-muted">
                    <Loader2 size={14} className="animate-spin" /> Working out who that is…
                  </span>
                ) : (
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <Users size={15} className="text-brand-600" />
                    <span className="type-label-strong text-content">{audience?.total ?? 0}</span>
                    <span className="type-body text-content-2">
                      {audience?.total === 1 ? 'person' : 'people'} — of whom
                    </span>
                    <span className="type-label-strong text-content">{audience?.reachable ?? 0}</span>
                    <span className="type-body text-content-2">
                      {audience?.reachable === 1 ? 'has' : 'have'} a registered device and can receive a push
                    </span>
                    {!!audience?.sample?.length && (
                      <span className="w-full type-caption text-muted">
                        e.g. {audience.sample.map((s) => s.fullName).filter(Boolean).join(', ')}
                        {(audience.total ?? 0) > audience.sample.length && ' …'}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {audienceReady && !countingAudience && audience?.reachable === 0 && (
              <Alert
                className="mt-3"
                type="warning"
                message="Nobody in this audience has a registered device"
                description="Every recipient would be recorded as skipped. They will still see it in the in-app inbox next time they sign in."
              />
            )}
          </Card>
        </div>

        {/* ── Preview ───────────────────────────────────────────────────────────────────── */}
        <div>
          <Card className="xl:sticky xl:top-20">
            <p className="mb-3 type-label-strong text-content">Preview</p>
            <NotificationPreview draft={draft} link={resolvedLink} />
          </Card>
        </div>
      </div>

      {/* ── History ───────────────────────────────────────────────────────────────────────── */}
      <Card>
        <p className="mb-3 type-label-strong text-content">Sent &amp; drafted</p>
        {!loadingCampaigns && !campaigns?.items?.length ? (
          <EmptyState
            title="No broadcasts yet"
            description="Compose one above. Drafts are kept here until you send them."
          />
        ) : (
          <DataTable
            storageKey="notif-campaigns"
            rowKey="id"
            loading={loadingCampaigns}
            dataSource={campaigns?.items ?? []}
            columns={columns}
            pagination={{ pageSize: 10 }}
            onRow={(r) => ({ onClick: () => setOpenCampaign(r) })}
          />
        )}
      </Card>

      <CampaignModal
        campaign={openCampaign}
        links={links}
        onClose={() => setOpenCampaign(null)}
        onReuse={loadDraft}
      />
    </div>
  );
}

// ── Campaign detail ─────────────────────────────────────────────────────────────────────────────

interface RecipientOutcome {
  id: string; userId: string | null; fullName: string; roleCode: string; mobile: string;
  profilePhotoUrl: string | null; status: string; sentAt: string | null; readAt: string | null;
  error: string | null;
}

/** The delivery outcomes, in the order an operator cares about them, each with its own colour. */
const OUTCOME_TONES: Array<{ key: 'sentCount' | 'failedCount' | 'skippedCount'; label: string; status: string; tone: keyof typeof TAG_TONE_HUE; hint: string }> = [
  { key: 'sentCount', label: 'Delivered to FCM', status: 'SENT', tone: 'green', hint: 'The provider accepted it' },
  { key: 'failedCount', label: 'Failed', status: 'FAILED', tone: 'red', hint: 'The provider refused it' },
  { key: 'skippedCount', label: 'Skipped', status: 'SKIPPED', tone: 'gold', hint: 'Never attempted — see why' },
];

/**
 * ONE BROADCAST, READ BACK — as a modal, opened by clicking its row.
 *
 * This was a 560px drawer sliding in over the composer: the message, the counts and the recipient
 * list were one flat column of grey text, so "what did we say, who did we say it to, and what
 * happened to each of them" — three different questions — looked like one paragraph. It is now a
 * centred dialog in three named blocks, each fact stated once (no database ids, no raw enums), with
 * the message shown the way the handset showed it rather than described.
 *
 * The recipient list is filterable BY OUTCOME, because the only reason to open a sent campaign is
 * usually "who didn't get it" — and the counts above are the filter, so the number you click and the
 * rows you get are the same population.
 */
function CampaignModal({
  campaign, links, onClose, onReuse,
}: {
  campaign: Campaign | null; links: LinkTarget[];
  onClose: () => void; onReuse: (c: Campaign) => void;
}) {
  const [outcome, setOutcome] = useState<string | null>(null);
  useEffect(() => setOutcome(null), [campaign?.id]);

  const { data: recipients = [], isLoading } = useQuery({
    queryKey: ['notif-campaign-recipients', campaign?.id],
    queryFn: () => request<RecipientOutcome[]>({ url: `/notifications/campaigns/${campaign!.id}/recipients` }),
    enabled: !!campaign && campaign.status !== 'DRAFT',
  });

  if (!campaign) return null;
  const sent = campaign.status !== 'DRAFT';
  const target = links.find((l) => l.code === campaign.deeplink)
    ?? links.find((l) => l.needsId && campaign.deeplink?.startsWith(l.code.slice(0, l.code.indexOf('{'))));
  const shown = outcome ? recipients.filter((r) => r.status === outcome) : recipients;

  return (
    <Modal
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      width={760}
      autoFocus={false}
      title={
        <span className="flex flex-wrap items-center gap-2">
          <span className="type-h4 text-content">{campaign.title}</span>
          <StatusBadge status={campaign.status} />
        </span>
      }
      description={sent
        ? `Sent ${formatIst(campaign.sentAt ?? campaign.createdAt)}`
        : `Drafted ${formatIst(campaign.createdAt)} — nothing has gone out`}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="default" onClick={onClose}>Close</Button>
          <Button variant="primary" onClick={() => onReuse(campaign)}>
            {campaign.status === 'DRAFT' ? 'Edit this draft' : 'Use as the basis for a new one'}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        {/* ── What people saw ──────────────────────────────────────────────────────────────── */}
        <section>
          <p className="mb-2 type-label-strong text-content">What people saw</p>
          <div className="overflow-hidden rounded-card border border-line bg-surface-2">
            <div className="flex items-start gap-2.5 p-3">
              <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-brand-600 text-white">
                <Bell size={16} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="type-caption text-muted">Green Bay Bio</p>
                <p className="type-label-strong text-content">{campaign.title}</p>
                <p className="whitespace-pre-wrap type-body text-content-2">{campaign.body}</p>
              </div>
            </div>
            {campaign.imageUrl && <PictureFrame url={fileUrl(campaign.imageUrl)} className="border-t border-line" />}
            {campaign.documentUrl && (
              <a
                href={fileUrl(campaign.documentUrl)}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 border-t border-line px-3 py-2.5 hover:bg-surface-3"
              >
                <FileText size={16} className="shrink-0 text-brand-600" />
                <span className="min-w-0 flex-1 truncate type-body text-content">
                  {campaign.documentName || 'Attached document'}
                </span>
                <Tag tone="blue">opens on tap</Tag>
              </a>
            )}
          </div>
          {campaign.deeplink && (
            <p className="mt-2 flex flex-wrap items-center gap-1.5 type-caption text-muted">
              <Link2 size={13} className="text-brand-600" />
              Tapping it opens
              <span className="type-caption-strong text-content-2">{target?.label ?? campaign.deeplink}</span>
              <Tag tone="neutral">/{campaign.deeplink}</Tag>
            </p>
          )}
        </section>

        {/* ── Who it went to ───────────────────────────────────────────────────────────────── */}
        <section>
          <p className="mb-2 type-label-strong text-content">Who it went to</p>
          <div className="flex flex-wrap items-center gap-1.5 rounded-card border border-line bg-surface-2 px-3 py-2.5">
            {campaign.audience === 'ALL' && (
              <span className="flex items-center gap-1.5 type-body text-content-2">
                <Users size={14} className="text-brand-600" /> Everyone active on the platform
              </span>
            )}
            {campaign.audience === 'BY_ROLE' && (
              <>
                <span className="type-body text-content-2">These roles:</span>
                {(campaign.roleCodes ?? []).map((c) => <RoleBadge key={c} role={c} />)}
              </>
            )}
            {campaign.audience === 'BY_TERRITORY' && (
              <span className="flex items-center gap-1.5 type-body text-content-2">
                <MapPin size={14} className="text-brand-600" />
                {campaign.territoryName || 'One territory'}, and everything beneath it
              </span>
            )}
            {campaign.audience === 'CUSTOM' && (() => {
              const names = campaign.audienceNames ?? [];
              const total = (campaign.userIds ?? []).length;
              const more = total - names.length;
              return (
                <span className="flex items-center gap-1.5 type-body text-content-2">
                  <Users size={14} className="text-brand-600" />
                  {names.length
                    ? <>{names.join(', ')}{more > 0 && <span className="text-muted"> and {more} more</span>}</>
                    : <>{total} named {total === 1 ? 'person' : 'people'}</>}
                </span>
              );
            })()}
          </div>
        </section>

        {campaign.error && (
          <Alert type="warning" message="Not everything went out" description={campaign.error} />
        )}

        {/* ── What happened — the counts ARE the filter ─────────────────────────────────────── */}
        {sent && (
          <section>
            <p className="mb-2 type-label-strong text-content">What happened</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {/*
                COLOUR SAYS WHAT THE NUMBER IS; THE RING SAYS WHAT IS FILTERING.
                Tinting a tile's border by its outcome and ALSO using a border to mark the selection
                made "has a count" and "is the current filter" look the same. The hue now lives in a
                dot and the figure, and only the filtering tile is tinted and ringed.
              */}
              <button
                type="button"
                onClick={() => setOutcome(null)}
                aria-pressed={outcome === null}
                className={cn('rounded-card border border-line px-3 py-2.5 text-left transition-all hover:shadow-sm',
                  outcome === null && 'bg-surface-2 ring-2 ring-brand-500/40')}
              >
                <p className="type-h4 text-content">{campaign.recipientCount}</p>
                <p className="type-caption text-muted">Addressed</p>
              </button>
              {OUTCOME_TONES.map((o) => {
                const n = campaign[o.key];
                const hue = TAG_TONE_HUE[o.tone];
                const on = outcome === o.status;
                return (
                  <button
                    key={o.key}
                    type="button"
                    disabled={n === 0}
                    onClick={() => setOutcome(on ? null : o.status)}
                    title={n === 0 ? `Nobody was ${o.label.toLowerCase()}` : o.hint}
                    aria-pressed={on}
                    className={cn('rounded-card border border-line px-3 py-2.5 text-left transition-all',
                      n === 0 ? 'opacity-55' : 'hover:shadow-sm',
                      on && 'ring-2')}
                    style={on ? { ...filterTint(hue), boxShadow: `0 0 0 2px color-mix(in srgb, ${hue} 40%, transparent)` } : undefined}
                  >
                    <p className="type-h4" style={{ color: n > 0 ? hue : undefined }}>{n}</p>
                    <p className="flex items-center gap-1.5 type-caption text-muted">
                      <span className="size-1.5 shrink-0 rounded-full" style={{ backgroundColor: n > 0 ? hue : 'currentColor' }} />
                      {o.label}
                    </p>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Each recipient ───────────────────────────────────────────────────────────────── */}
        {sent && (
          <section>
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="type-label-strong text-content">
                {outcome ? `${shown.length} ${outcome.toLowerCase()}` : 'Every recipient'}
              </p>
              {outcome && (
                <Button size="sm" variant="ghost" onClick={() => setOutcome(null)}>
                  <X size={13} /> Show all
                </Button>
              )}
            </div>
            {isLoading && <p className="type-caption text-muted">Loading…</p>}
            {!isLoading && shown.length === 0 && (
              <p className="type-caption text-muted">Nobody in this outcome.</p>
            )}
            <div className="max-h-80 space-y-1 overflow-y-auto pr-0.5">
              {shown.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center gap-2.5 rounded-card border border-line px-2.5 py-2"
                  style={{ borderLeftWidth: 3, borderLeftColor: TAG_TONE_HUE[roleTone(r.roleCode)] }}
                >
                  <EntityAvatar name={r.fullName || r.mobile} src={r.profilePhotoUrl ?? undefined} size={28} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate type-body text-content">{r.fullName || r.mobile}</span>
                    {r.error && <span className="block truncate type-caption text-warning-600">{r.error}</span>}
                  </span>
                  <RoleBadge role={r.roleCode} />
                  {r.readAt && <Tag tone="blue">read</Tag>}
                  <StatusBadge status={r.status} />
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </Modal>
  );
}
